import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Gavel, Mail, ChevronDown, ChevronRight, CheckCircle2, Search,
  Loader2, AlertTriangle, RefreshCw, Calendar, Trash2, FileDown, FileText, Package, Printer, UserCog,
} from "lucide-react";
import { ReassignDialog } from "./ReassignDialog";
import { JudgeOptionLabel } from "./JudgeOptionLabel";
import { ActNumberButton } from "./ActNumberButton";
import { downloadReassignmentAct, downloadActFromUrl } from "./actDownloads";
import { ActIssueDialog } from "./ActIssueDialog";
import { DateInput } from "./ui/date-input";

// Ημερομηνίες Έκδοσης ανά υπόθεση (case_id → YYYY-MM-DD) από το preview
const datesFromPreview = (rows) => Object.fromEntries((rows || []).map((c) => [c.case_id, (c.issue_date || "").slice(0, 10)]));

const fmt = (d) => {
  if (!d) return "—";
  const parts = String(d).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

// Υπόθεση «Εν αναμονή»: έχει ανατεθεί δικαστής αλλά ΔΕΝ έχει κλείσει η προσθεσμία
// προσθήκης ακόμη (σήμερα < judge_start, όπου judge_start = προσθήκη_end + 1 εργ. ημ.).
// Σε αυτή την περίπτωση η Πράξη Ορισμού πρέπει να εκδοθεί την judge_start και όχι νωρίτερα.
const isPendingAssignment = (c) => {
  if (!c || !c.assigned_judge || !c.judge_start) return false;
  const start = new Date(`${c.judge_start}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime() < start.getTime();
};

// Χρώματα επείγοντος ορισμού δικαστή με βάση πόσες ημέρες έχουν περάσει
// από την έναρξη του 10ημέρου (judge_start).
// 1η–3η ημέρα → πράσινο, 4η–9η → κίτρινο, 10η+ → κόκκινο.
const urgencyForJudgeWindow = (judgeStart, judgeEnd) => {
  if (!judgeStart) return { label: "", cls: "", days: null };
  const start = new Date(`${judgeStart}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - start.getTime();
  const daysIn = Math.floor(diffMs / 86400000) + 1; // 1-based: ημέρα 1 = start day
  if (daysIn < 1) return { label: "Προσεχώς", cls: "bg-slate-100 text-slate-600 border-slate-200", days: daysIn };
  if (daysIn <= 3) return { label: `Ημέρα ${daysIn}/10`, cls: "bg-emerald-100 text-emerald-800 border-emerald-300", days: daysIn };
  if (daysIn <= 9) return { label: `Ημέρα ${daysIn}/10`, cls: "bg-amber-100 text-amber-800 border-amber-300", days: daysIn };
  if (daysIn === 10) return { label: "Ημέρα 10/10 — Τελευταία", cls: "bg-red-100 text-red-800 border-red-300", days: daysIn };
  // Μετά τη 10η: ληγμένο
  return { label: `Ληγμένη +${daysIn - 10} ημ.`, cls: "bg-red-600 text-white border-red-700", days: daysIn };
};

// Χρώμα κάρτας ολόκληρης δικασίμου = αθροιστικό «χειρότερο» χρώμα των υποθέσεων
const aggregateUrgency = (cases) => {
  let worst = 0; // 0 = none, 1 green, 2 amber, 3 red
  for (const c of cases) {
    if (c.assigned_judge) continue; // ολοκληρωμένες δεν μετρούν
    const u = urgencyForJudgeWindow(c.judge_start, c.judge_end);
    if (u.days == null || u.days < 1) continue;
    if (u.days <= 3) worst = Math.max(worst, 1);
    else if (u.days <= 9) worst = Math.max(worst, 2);
    else worst = Math.max(worst, 3);
  }
  if (worst === 1) return "border-emerald-300 bg-emerald-50/40";
  if (worst === 2) return "border-amber-300 bg-amber-50/40";
  if (worst === 3) return "border-red-300 bg-red-50/40";
  return "";
};

// Αθροιστικά counts χρωμάτων ανά λίστα υποθέσεων
const urgencyCounts = (cases) => {
  let green = 0, amber = 0, red = 0, assigned = 0, future = 0, pending = 0;
  for (const c of cases) {
    if (c.assigned_judge) {
      if (isPendingAssignment(c)) pending++;
      else assigned++;
      continue;
    }
    const u = urgencyForJudgeWindow(c.judge_start, c.judge_end);
    if (u.days == null || u.days < 1) future++;
    else if (u.days <= 3) green++;
    else if (u.days <= 9) amber++;
    else red++;
  }
  return { green, amber, red, assigned, future, pending };
};

// Inline counters component: 🟢 X · 🟡 Y · 🔴 Z (πάντα εμφανείς, μηδενικά γκρι)
const UrgencyCounters = ({ counts }) => {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono">
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border ${counts.green > 0 ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{counts.green}
      </span>
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border ${counts.amber > 0 ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{counts.amber}
      </span>
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border ${counts.red > 0 ? "bg-red-100 text-red-800 border-red-300" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />{counts.red}
      </span>
      {counts.pending > 0 && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300"
          title="Έχει ανατεθεί δικαστής αλλά δεν έχει κλείσει η προσθεσμία προσθήκης — η Πράξη Ορισμού δεν μπορεί να εκδοθεί ακόμη."
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Εν αναμονή {counts.pending}
        </span>
      )}
    </span>
  );
};

const JudgeAssignmentTab = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const anon = (t) => privacy ? anonymize(t) : t;

  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const defaultCourt = userCourtTypes.length === 1 ? userCourtTypes[0] : "Μονομελές";

  // Φίλτρα
  const [filingFrom, setFilingFrom] = useState("2026-01-01");
  const [filingTo, setFilingTo] = useState("");
  const [courtType, setCourtType] = useState(defaultCourt);
  const [pinakioSearch, setPinakioSearch] = useState("");

  const [groupsRaw, setGroupsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  // Δύο επίπεδα expand: ανά πινάκιο και ανά δικάσιμο εντός πινακίου
  const [expandedPinakio, setExpandedPinakio] = useState({});
  const [expandedHearing, setExpandedHearing] = useState({});
  const [selectedIds, setSelectedIds] = useState({});

  // Judges
  const [judgesList, setJudgesList] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailJudgeName, setEmailJudgeName] = useState("");
  const [emailJudgeAddr, setEmailJudgeAddr] = useState("");
  const [emailAutoAssign, setEmailAutoAssign] = useState(true);
  const [emailAttachCompActs, setEmailAttachCompActs] = useState(true);  // Πολυμελές: συνημμένες Πράξεις Ανάθεσης σε Σύνθεση
  const [emailTextOnly, setEmailTextOnly] = useState(false);              // μόνο κείμενο, χωρίς συνημμένα
  const [bulkAssignNote, setBulkAssignNote] = useState(true);              // «Επισημαίνεται ότι…» στις μαζικές Πράξεις
  const [bulkRappNote, setBulkRappNote] = useState(true);
  const [emailPreview, setEmailPreview] = useState("");
  const [sending, setSending] = useState(false);

  // Delete pinakio (admin only)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pinakioToDelete, setPinakioToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk Πράξεις Ορισμού Εισηγητή (μόνο Πολυμελές)
  const [bulkRappOpen, setBulkRappOpen] = useState(false);
  const [bulkRappCtx, setBulkRappCtx] = useState({ pinakio: "", hearing_date: "" });
  const [bulkRappPreview, setBulkRappPreview] = useState(null);
  const [bulkRappLoadingPrev, setBulkRappLoadingPrev] = useState(false);
  const [bulkRappStartNum, setBulkRappStartNum] = useState("");
  const [bulkRappYear, setBulkRappYear] = useState(String(new Date().getFullYear()));
  const [bulkRappFormat, setBulkRappFormat] = useState("docx");
  const [bulkRappBusy, setBulkRappBusy] = useState(false);
  const [bulkRappIssueDates, setBulkRappIssueDates] = useState({});

  // Bulk Πράξεις Ορισμού Δικαστή (Μονομελές/Πολυμελές) + auto-email + κλείδωμα
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignCtx, setBulkAssignCtx] = useState({ pinakio: "", hearing_date: "" });
  const [bulkAssignPreview, setBulkAssignPreview] = useState(null);
  const [bulkAssignLoadingPrev, setBulkAssignLoadingPrev] = useState(false);
  const [bulkAssignStartNum, setBulkAssignStartNum] = useState("");
  const [bulkAssignYear, setBulkAssignYear] = useState(String(new Date().getFullYear()));
  const [bulkAssignFormat, setBulkAssignFormat] = useState("docx");
  const [bulkAssignJudgeId, setBulkAssignJudgeId] = useState("");
  const [bulkAssignAutoEmail, setBulkAssignAutoEmail] = useState(true);
  const [bulkAssignExtraEmails, setBulkAssignExtraEmails] = useState("");  // comma-separated
  const [bulkAssignCcEmails, setBulkAssignCcEmails] = useState("");          // comma-separated
  const [bulkAssignJudges, setBulkAssignJudges] = useState([]);
  const [bulkAssignBusy, setBulkAssignBusy] = useState(false);
  const [bulkAssignIssueDates, setBulkAssignIssueDates] = useState({});
  // Έκδοση Πράξης ανά υπόθεση (Αρ. Πράξης + Ημερομηνία Έκδοσης)
  const [actDialog, setActDialog] = useState({ open: false, c: null, format: "docx" });

  const openBulkAssignment = async (pinakioLabel, hearingDate, hearingCases = [], autoEmail = true) => {
    // Αν ο χρήστης έχει επιλέξει checkboxes σε αυτή τη δικάσιμο, τα στέλνουμε στο
    // preview/POST ώστε η bulk ενέργεια να αφορά ΜΟΝΟ τις επιλεγμένες υποθέσεις
    // (όχι όλη τη δικάσιμο). Αν δεν έχει επιλέξει τίποτα, λειτουργεί ως πριν (όλες).
    const selectedInHearing = (hearingCases || [])
      .filter(c => selectedIds[c.id])
      .map(c => c.id);
    setBulkAssignCtx({
      pinakio: pinakioLabel,
      hearing_date: hearingDate,
      case_ids: selectedInHearing,
    });
    setBulkAssignOpen(true);
    setBulkAssignPreview(null);
    setBulkAssignJudgeId("");
    setBulkAssignAutoEmail(autoEmail);
    setBulkAssignIssueDates({});
    setBulkAssignLoadingPrev(true);
    try {
      const previewParams = {
        court_type: courtType,
        pinakio_label: pinakioLabel,
        hearing_date: hearingDate,
      };
      if (selectedInHearing.length > 0) {
        previewParams.case_ids = selectedInHearing.join(",");
      }
      // Decoupled fetches — αν αποτύχει το ένα, το άλλο συνεχίζει
      let previewData = null;
      try {
        const previewRes = await axios.get(`${API}/pretrial-tools/bulk-assignment-preview`, {
          params: previewParams,
        });
        previewData = previewRes.data;
      } catch (err1) {
        const detail = err1?.response?.data?.detail || err1?.message || "Σφάλμα φόρτωσης preview";
        toast.error(`Preview: ${detail}`);
      }
      if (previewData) { setBulkAssignPreview(previewData); setBulkAssignIssueDates(datesFromPreview(previewData.cases)); }

      try {
        const judgesRes = await axios.get(`${API}/pretrial-judges`, { params: { court_type: courtType } });
        setBulkAssignJudges(judgesRes.data?.judges || []);
      } catch (err2) {
        // Δεν είναι κρίσιμο — απλά δεν θα φορτωθεί η λίστα δικαστών
        console.warn("pretrial-judges fetch failed", err2);
        setBulkAssignJudges([]);
      }

      // Auto-suggest επόμενο διαθέσιμο Αρ. Πράξης (per court_type + per branch).
      // ΣΗΜΑΝΤΙΚΟ: Στέλνουμε `pinakio_label` ώστε το backend να εξάγει αυτόματα
      // το σωστό branch από τα pinakio_branch_mappings (πχ '160-ΚΑΛ' → 'Καλλιθέας').
      // Έτσι ο admin που επεξεργάζεται οποιοδήποτε πινάκιο παίρνει τον σωστό
      // μετρητή ανά κατάστημα (κάθε κατάστημα έχει δικό του βιβλίο).
      try {
        const nextRes = await axios.get(`${API}/pretrial-sectors/next-act-number`, {
          params: {
            court_type: courtType,
            year: String(new Date(hearingDate).getFullYear() || new Date().getFullYear()),
            pinakio_label: pinakioLabel,
            // Backup hint από τον χρήστη — χρησιμοποιείται μόνο αν το pinakio_label δεν μπορεί να resolved
            branch: (user?.court_branch || "").trim(),
          },
        });
        const nextNum = nextRes.data?.next;
        if (nextNum && Number.isInteger(nextNum)) {
          setBulkAssignStartNum(String(nextNum));
        } else {
          setBulkAssignStartNum("");
        }
      } catch (err3) {
        console.warn("next-act-number fetch failed", err3);
        setBulkAssignStartNum("");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα preview");
    } finally { setBulkAssignLoadingPrev(false); }
  };

  const runBulkAssignment = async () => {
    if (!bulkAssignPreview || (bulkAssignPreview.total || 0) === 0) {
      toast.error("Δεν υπάρχουν υποθέσεις"); return;
    }
    // Αν δεν δοθεί αρχικός αριθμός, στέλνουμε 1 — ο backend δεν επαναριθμεί
    // αυτόματα bulk (χρησιμοποιεί τα υπάρχοντα act_number ή placeholder "_____").
    const startNum = (bulkAssignStartNum && parseInt(bulkAssignStartNum, 10) >= 1)
      ? parseInt(bulkAssignStartNum, 10)
      : 1;
    const missingDates = (bulkAssignPreview.cases || []).filter(c => (c.eligible || c.has_act) && !bulkAssignIssueDates[c.case_id]).length;
    if (missingDates > 0) { toast.error(`Συμπληρώστε Ημερομηνία Έκδοσης σε ${missingDates} υπόθεση/εις`); return; }
    setBulkAssignBusy(true);
    try {
      const res = await axios.post(
        `${API}/pretrial-tools/bulk-assignment-acts`,
        {
          court_type: courtType,
          pinakio_label: bulkAssignCtx.pinakio,
          hearing_date: bulkAssignCtx.hearing_date,
          starting_act_number: startNum,
          act_year: bulkAssignYear,
          format: bulkAssignFormat,
          pretrial_note: bulkAssignNote,
          judge_pretrial_id: bulkAssignJudgeId || null,
          auto_email: bulkAssignAutoEmail,
          extra_recipients: bulkAssignExtraEmails
            ? bulkAssignExtraEmails.split(",").map(s => s.trim()).filter(s => s && s.includes("@"))
            : [],
          cc_recipients: bulkAssignCcEmails
            ? bulkAssignCcEmails.split(",").map(s => s.trim()).filter(s => s && s.includes("@"))
            : [],
          case_ids: (bulkAssignCtx.case_ids && bulkAssignCtx.case_ids.length > 0)
            ? bulkAssignCtx.case_ids
            : [],
          issue_dates: bulkAssignIssueDates,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `praxeis_orismou_${bulkAssignCtx.pinakio}_${bulkAssignCtx.hearing_date}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      const generated = res.headers["x-generated"] || "?";
      const skipped = res.headers["x-skipped"] || "0";
      const emSec = res.headers["x-emailed-secretariat"] === "True" ? "✉ Γραμμ." : "";
      const emJdg = res.headers["x-emailed-judge"] === "True" ? "✉ Δικαστής" : "";
      const emExtra = res.headers["x-emailed-extra"] === "True" ? "✉ Επιπλέον" : "";
      const emErrors = res.headers["x-email-errors"] || "";
      const sentLabel = [emSec, emJdg, emExtra].filter(Boolean).join(" · ");
      if (emErrors && bulkAssignAutoEmail) {
        toast.warning(`ZIP: ${generated} πράξεις, ${skipped} παραλείψεις. ${sentLabel || "✉ — κανένα email"}. Σφάλματα: ${emErrors}`, { duration: 15000 });
      } else {
        toast.success(`ZIP: ${generated} πράξεις, ${skipped} παραλείψεις. ${sentLabel}`);
      }
      setBulkAssignOpen(false);
      fetchPinakia();
    } catch (e) {
      let msg = "Σφάλμα";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* ignore */ }
      } else if (e.response?.data?.detail) msg = e.response.data.detail;
      toast.error(msg);
    } finally { setBulkAssignBusy(false); }
  };

  const downloadBulletin = async (pinakioLabel, hearingDate, fmtKind) => {
    try {
      const params = new URLSearchParams({
        court_type: courtType, pinakio_label: pinakioLabel, hearing_date: hearingDate, format: fmtKind,
      });
      const res = await axios.get(`${API}/pretrial-tools/pinakio-bulletin?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `deltio_${pinakioLabel}_${hearingDate}.${fmtKind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Δελτίο έτοιμο");
    } catch (e) {
      let msg = "Σφάλμα";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* ignore */ }
      }
      toast.error(msg);
    }
  };

  const openBulkRapporteur = async (pinakioLabel, hearingDate) => {
    setBulkRappCtx({ pinakio: pinakioLabel, hearing_date: hearingDate });
    setBulkRappOpen(true);
    setBulkRappPreview(null);
    setBulkRappLoadingPrev(true);
    try {
      const r = await axios.get(`${API}/pretrial-tools/bulk-rapporteur-preview`, {
        params: { court_type: "Πολυμελές", pinakio_label: pinakioLabel, hearing_date: hearingDate },
      });
      setBulkRappPreview(r.data);
      setBulkRappIssueDates(datesFromPreview(r.data?.cases));
      // Δεν προτείνουμε αρ. πράξης — ο χρήστης τον εισάγει χειροκίνητα
      setBulkRappStartNum("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα preview");
    } finally { setBulkRappLoadingPrev(false); }
  };

  const runBulkRapporteur = async () => {
    if (!bulkRappPreview || bulkRappPreview.eligible === 0) {
      toast.error("Δεν υπάρχουν επιλέξιμες υποθέσεις"); return;
    }
    if (!bulkRappStartNum || bulkRappStartNum < 1) { toast.error("Δώστε αρχικό αριθμό πράξης"); return; }
    const missingRapp = (bulkRappPreview.cases || []).filter(c => c.eligible && !bulkRappIssueDates[c.case_id]).length;
    if (missingRapp > 0) { toast.error(`Συμπληρώστε Ημερομηνία Έκδοσης σε ${missingRapp} υπόθεση/εις`); return; }
    setBulkRappBusy(true);
    try {
      const res = await axios.post(
        `${API}/pretrial-tools/bulk-rapporteur-acts`,
        {
          court_type: "Πολυμελές",
          pinakio_label: bulkRappCtx.pinakio,
          hearing_date: bulkRappCtx.hearing_date,
          starting_act_number: parseInt(bulkRappStartNum, 10),
          act_year: bulkRappYear,
          format: bulkRappFormat,
          issue_dates: bulkRappIssueDates,
          pretrial_note: bulkRappNote,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `praxeis_eishghth_${bulkRappCtx.pinakio}_${bulkRappCtx.hearing_date}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      const generated = res.headers["x-generated"] || "?";
      const skipped = res.headers["x-skipped"] || "0";
      toast.success(`ZIP έτοιμο: ${generated} πράξεις, ${skipped} παραλείψεις`);
      setBulkRappOpen(false);
      fetchPinakia();
    } catch (e) {
      let msg = "Σφάλμα";
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* ignore */ }
      } else if (e.response?.data?.detail) msg = e.response.data.detail;
      toast.error(msg);
    } finally { setBulkRappBusy(false); }
  };

  const confirmDeletePinakio = (pinakio) => {
    setPinakioToDelete(pinakio);
    setShowDeleteDialog(true);
  };

  const handleDeletePinakio = async () => {
    if (!pinakioToDelete) return;
    setDeleting(true);
    try {
      const ids = pinakioToDelete.hearings.flatMap(h => h.cases.map(c => c.id));
      const res = await axios.post(`${API}/cases/bulk-delete`, { case_ids: ids });
      toast.success(`Διαγράφηκαν ${res.data?.deleted || ids.length} υποθέσεις του πινακίου ${pinakioToDelete.pinakio_label}`);
      setShowDeleteDialog(false);
      setPinakioToDelete(null);
      fetchPinakia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία διαγραφής");
    } finally {
      setDeleting(false);
    }
  };

  // Export Excel/PDF helpers
  const downloadExport = async (format, { pinakio = "", hearing_date = "" } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("format", format);
      if (pinakio) params.append("pinakio", pinakio);
      if (hearing_date) params.append("hearing_date", hearing_date);
      if (courtType) params.append("court_type", courtType);
      const res = await axios.get(
        `${API}/pretrial-tools/judge-assignment-export?${params.toString()}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `judge_assignment.${format}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Εξαγωγή ${format.toUpperCase()} ολοκληρώθηκε`);
    } catch (e) {
      toast.error("Σφάλμα εξαγωγής: " + (e.response?.data?.detail || e.message));
    }
  };

  // Ορισμός Δικαστή Προδικαστικού Ελέγχου ανά Πινάκιο + Δικάσιμο
  const [judgePickerState, setJudgePickerState] = useState({ open: false, pinakio_label: "", hearing_date: "", judge_id: "", saving: false });
  const [reassignTarget, setReassignTarget] = useState(null);
  const [pretrialJudgesList, setPretrialJudgesList] = useState([]);

  // Φόρτωση λίστας δικαστών όταν αλλάζει courtType
  useEffect(() => {
    axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(courtType)}`)
      .then(r => setPretrialJudgesList(r.data?.judges || []))
      .catch(() => setPretrialJudgesList([]));
  }, [courtType]);

  const assignJudgeToPinakioHearing = (pinakioLabel, hearingDate) => {
    setJudgePickerState({ open: true, pinakio_label: pinakioLabel, hearing_date: hearingDate, judge_id: "", saving: false });
  };

  const confirmAssignJudgeFromPicker = async () => {
    const j = pretrialJudgesList.find(x => x.id === judgePickerState.judge_id);
    if (!j) { toast.error("Επιλέξτε δικαστή"); return; }
    try {
      setJudgePickerState(s => ({ ...s, saving: true }));
      const res = await axios.post(`${API}/pretrial-tools/assign-judge-by-pinakio`, {
        pinakio_label: judgePickerState.pinakio_label,
        hearing_date: judgePickerState.hearing_date,
        court_type: courtType,
        judge_name: j.full_name,
        judge_id: j.id,
        judge_grade: j.grade,
        judge_email: j.email,
      });
      toast.success(res.data?.message || `Ορίστηκε ${j.full_name}`);
      setJudgePickerState({ open: false, pinakio_label: "", hearing_date: "", judge_id: "", saving: false });
      await fetchPinakia();
    } catch (e) {
      toast.error("Σφάλμα ανάθεσης: " + (e.response?.data?.detail || e.message));
      setJudgePickerState(s => ({ ...s, saving: false }));
    }
  };

  // ===== Εκτύπωση Όλων των Πράξεων Δικασίμου (single PDF) =====
  const printAllActs = async (pinakioLabel, hearingDate) => {
    const duplexSafe = window.confirm(
      "Εκτύπωση Διπλής Όψης (Duplex);\n\n" +
      "✓ ΟΚ = Διπλή όψη (προσθέτει κενή σελίδα μετά από κάθε πράξη ώστε η επόμενη να ξεκινά σε νέο φύλλο)\n" +
      "✗ Άκυρο = Απλή εκτύπωση (μία πράξη μετά την άλλη χωρίς κενές σελίδες)"
    );
    toast.info(`Παραγωγή PDF (${duplexSafe ? "διπλή όψη" : "απλή"})...`);
    let res;
    try {
      res = await axios.post(`${API}/pretrial-tools/print-all-acts`, {
        court_type: courtType,
        pinakio_label: pinakioLabel,
        hearing_date: hearingDate,
        duplex_safe: duplexSafe,
      }, { responseType: "blob", timeout: 300000 });
    } catch (e) {
      let msg = "Σφάλμα εκτύπωσης";
      try {
        const blob = e?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).detail || msg; } catch (_) { msg = txt || msg; }
        }
      } catch (_) { /* ignore */ }
      toast.error(msg, { duration: 10000 });
      return;
    }
    try {
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      // Άνοιγμα σε νέο tab για εκτύπωση
      window.open(blobUrl, "_blank");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `praxeis_${pinakioLabel}_${hearingDate}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
      toast.success(`PDF έτοιμο: ${res.headers["x-generated"] || "?"} πράξεις`);
    } catch (e) {
      toast.error("Σφάλμα κατεβάσματος: " + (e.message || ""));
    }
  };

  // ===== Απαλοιφή Ανάθεσης ΑΝΑ ΥΠΟΘΕΣΗ (single case) =====
  const clearCaseAssignment = async (c) => {
    const gakStr = `${c.gak_number}/${c.gak_year}`;
    if (!window.confirm(`Απαλοιφή ανάθεσης για την υπόθεση ΓΑΚ ${gakStr};\n\nΘα αφαιρεθεί ο δικαστής "${c.assigned_judge}" και η Πράξη Ορισμού (με audit log). Άλλες υποθέσεις του πινακίου ΔΕΝ επηρεάζονται.`)) return;
    try {
      const r = await axios.post(`${API}/pretrial-tools/cancel-assignment`, {
        case_id: c.id,
        reason: "Απαλοιφή από καρτέλα Διαχείρισης Ορισμού (per case)"
      });
      toast.success(`Απαλείφθηκε η ανάθεση για ΓΑΚ ${gakStr}: ${r.data?.previous_judge || ""}`);
      await fetchPinakia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα απαλοιφής ανάθεσης");
    }
  };

  // ===== Επανέκδοση Πράξεων Δικασίμου (ίδιοι αριθμοί, σωστές ημερομηνίες) =====
  const regenerateActsZip = async (pinakioLabel, hearingDate) => {
    if (!window.confirm(`Επανέκδοση όλων των Πράξεων του πινακίου «${pinakioLabel}» για τη δικάσιμο ${hearingDate};\n\nΟι αριθμοί πράξης ΘΑ ΔΙΑΤΗΡΗΘΟΥΝ, αλλά οι ημερομηνίες υπογραφής θα διορθωθούν στη σωστή (1η μέρα μετά κλείσιμο Προσθήκης ανά υπόθεση).`)) return;
    toast.info("Επανέκδοση πράξεων με διορθωμένες ημερομηνίες...");
    let res;
    try {
      res = await axios.post(`${API}/pretrial-tools/regenerate-existing-acts`, {
        court_type: courtType,
        pinakio_label: pinakioLabel,
        hearing_date: hearingDate,
        format: "docx",
      }, { responseType: "blob", timeout: 300000 });
    } catch (e) {
      let msg = "Σφάλμα επανέκδοσης";
      try {
        const blob = e?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { msg = JSON.parse(txt).detail || msg; } catch (_) { msg = txt || msg; }
        }
      } catch (_) { /* ignore */ }
      toast.error(msg, { duration: 10000 });
      return;
    }
    try {
      const blob = new Blob([res.data], { type: "application/zip" });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `epanekdosi_${pinakioLabel}_${hearingDate}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Επανέκδοση ολοκληρώθηκε: ${res.headers["x-regenerated"] || "?"} πράξεις`);
      await fetchPinakia();
    } catch (e) {
      toast.error("Σφάλμα κατεβάσματος: " + (e.message || ""));
    }
  };

  // ===== Απαλοιφή Ανάθεσης Δικαστή ανά Πινάκιο+Δικάσιμο =====
  const [clearJudgeState, setClearJudgeState] = useState({ open: false, pinakio_label: "", hearing_date: "", mode: "soft", saving: false });

  const openClearJudgeDialog = (pinakioLabel, hearingDate) => {
    setClearJudgeState({ open: true, pinakio_label: pinakioLabel, hearing_date: hearingDate, mode: "soft", saving: false });
  };

  const confirmClearJudge = async () => {
    try {
      setClearJudgeState(s => ({ ...s, saving: true }));
      const res = await axios.post(`${API}/pretrial-tools/clear-judge-by-pinakio`, {
        pinakio_label: clearJudgeState.pinakio_label,
        hearing_date: clearJudgeState.hearing_date,
        court_type: courtType,
        mode: clearJudgeState.mode,
      });
      toast.success(res.data?.message || "Έγινε απαλοιφή");
      setClearJudgeState({ open: false, pinakio_label: "", hearing_date: "", mode: "soft", saving: false });
      await fetchPinakia();
    } catch (e) {
      toast.error("Σφάλμα απαλοιφής: " + (e.response?.data?.detail || e.message));
      setClearJudgeState(s => ({ ...s, saving: false }));
    }
  };

  // ===== Εξαγωγή ZIP Πράξεων Δικασίμου ΧΩΡΙΣ Email — ίδιος διάλογος (Αρ. Πράξης + Ημερομηνία Έκδοσης ανά υπόθεση), auto-email off
  const exportActsZipNoEmail = (pinakioLabel, hearingDate, hearingCases = []) =>
    openBulkAssignment(pinakioLabel, hearingDate, hearingCases, false);

  // Εξαγωγή Ημερολογίου πινακίου + δικασίμου (XLSX / PDF)
  const exportPinakioCalendar = async (pinakioLabel, hearingDate, format = "xlsx") => {
    try {
      const params = new URLSearchParams({
        pinakio_label: pinakioLabel, hearing_date: hearingDate,
        court_type: courtType, format,
      });
      const res = await axios.get(`${API}/pretrial-tools/pinakio-calendar-export?${params.toString()}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `pinakio_calendar.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Ημερολόγιο ${format.toUpperCase()} έτοιμο`);
    } catch (e) {
      toast.error("Σφάλμα εξαγωγής ημερολογίου: " + (e.response?.data?.detail || e.message));
    }
  };

  // Πράξη Ορισμού Δικαστή ανά υπόθεση (DOCX/PDF/ODT) — Αρ. Πράξης + Ημερομηνία Έκδοσης στον διάλογο
  const generateAct = (c, format = "docx") => setActDialog({ open: true, c, format });
  const confirmGenerateAct = async ({ actNumber, issueDate, format, includeNote }) => {
    const c = actDialog.c;
    const year = String(c.gak_year || new Date().getFullYear());
    const params = { act_number: actNumber, issue_date: issueDate };
    if (includeNote !== undefined) params.pretrial_note = includeNote;
    const ok = await downloadActFromUrl(`${API}/pretrial-sectors/generate-act/${c.id}`, params, format,
      `Πράξη Ορισμού ${actNumber}/${year}`);
    if (ok) {
      c.assignment_act_number = actNumber;
      c.assignment_act_year = year;
      c.assignment_act_issue_date = issueDate;
      setGroupsRaw(prev => [...prev]);
    }
    return ok;
  };

  useEffect(() => {
    fetchPinakia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch judges όταν αλλάζει ο τύπος δικαστηρίου (Μονομελές/Πολυμελές)
  useEffect(() => {
    fetchJudges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtType]);

  const fetchJudges = async () => {
    try {
      // Χρήση του σωστού endpoint: pretrial_judges collection έχει ΟΛΟΥΣ τους δικαστές
      // που έχουν καταχωρηθεί ως δικαστές προδικαστικού (όχι μόνο user accounts).
      // Φιλτράρουμε per court_type ώστε να εμφανίζονται ΜΟΝΟ Μονομελούς/Πολυμελούς δικαστές.
      const params = new URLSearchParams();
      if (courtType) params.append("court_type", courtType);
      const res = await axios.get(`${API}/pretrial-judges?${params.toString()}`);
      const data = res.data?.judges || res.data || [];
      // Backup fallback: αν δεν υπάρχουν pretrial_judges, παίρνουμε users με role=judge
      let judges = data;
      if (!Array.isArray(judges) || judges.length === 0) {
        try {
          const r2 = await axios.get(`${API}/users`);
          judges = (r2.data || []).filter(u =>
            u.role === "judge" || u.position?.toLowerCase()?.includes("δικαστ")
          );
        } catch { /* ignore */ }
      }
      setJudgesList(judges);
    } catch (err) {
      console.warn("fetchJudges failed", err);
      setJudgesList([]);
    }
  };

  const fetchPinakia = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filingFrom) params.append("filing_from", filingFrom);
      if (filingTo) params.append("filing_to", filingTo);
      if (courtType) params.append("court_type", courtType);
      if (pinakioSearch) params.append("pinakio_search", pinakioSearch);
      const res = await axios.get(`${API}/pretrial-tools/judge-assignment-pinakia?${params.toString()}`);
      setGroupsRaw(res.data?.pinakia || []);
    } catch {
      toast.error("Σφάλμα φόρτωσης πινακίων");
    } finally {
      setLoading(false);
    }
  };

  // Επίπεδο 1: ομαδοποίηση ανά Πινάκιο (συνολικά πινάκια ως cards collapsed).
  // Επίπεδο 2 (μέσα): ανά Δικάσιμο.
  const pinakiaTree = useMemo(() => {
    const byPinakio = {};
    for (const g of groupsRaw) {
      const key = g.pinakio_label;
      if (!byPinakio[key]) {
        byPinakio[key] = {
          pinakio_label: key,
          court_type: g.court_type,
          hearings: [],
          total_cases: 0,
          assigned_count: 0,
          email_sent_count: 0,
        };
      }
      byPinakio[key].hearings.push({
        hearing_date: g.hearing_date,
        group_key: g.group_key,
        cases: g.cases || [],
        total_cases: g.total_cases || 0,
        assigned_count: g.assigned_count || 0,
        email_sent_count: g.email_sent_count || 0,
      });
      byPinakio[key].total_cases += g.total_cases || 0;
      byPinakio[key].assigned_count += g.assigned_count || 0;
      byPinakio[key].email_sent_count += g.email_sent_count || 0;
    }
    return Object.values(byPinakio)
      .map(p => ({
        ...p,
        hearings: p.hearings.sort((a, b) =>
          (a.hearing_date || "9999").localeCompare(b.hearing_date || "9999")
        ),
      }))
      .sort((a, b) => a.pinakio_label.localeCompare(b.pinakio_label));
  }, [groupsRaw]);

  const togglePinakio = (label) => setExpandedPinakio(p => ({ ...p, [label]: !p[label] }));
  const toggleHearing = (key) => setExpandedHearing(p => ({ ...p, [key]: !p[key] }));

  const toggleCase = (id) => setSelectedIds(prev => {
    const n = { ...prev };
    if (n[id]) delete n[id]; else n[id] = true;
    return n;
  });

  const toggleHearingCases = (cases, value) => {
    setSelectedIds(prev => {
      const n = { ...prev };
      cases.forEach(c => { if (value) n[c.id] = true; else delete n[c.id]; });
      return n;
    });
  };

  const togglePinakioCases = (pinakio, value) => {
    setSelectedIds(prev => {
      const n = { ...prev };
      pinakio.hearings.forEach(h => h.cases.forEach(c => { if (value) n[c.id] = true; else delete n[c.id]; }));
      return n;
    });
  };

  const selectedCount = Object.keys(selectedIds).length;
  const selectedCases = useMemo(() => {
    const arr = [];
    groupsRaw.forEach(g => g.cases.forEach(c => { if (selectedIds[c.id]) arr.push(c); }));
    return arr;
  }, [groupsRaw, selectedIds]);

  const buildEmailPreview = () => {
    const rows = selectedCases.map((c, i) => {
      const gakak = `${c.gak_number}/${c.eak_number || "-"}`;
      return `${String(i + 1).padEnd(4)}${gakak.padEnd(22)}${(c.gak_year || "").padEnd(8)}${fmt(c.filing_date).padEnd(14)}${fmt(c.pretrial_end).padEnd(14)}`;
    });
    // Branch header ΠΡΩΤΗ γραμμή — πρέπει να ταιριάζει 1-1 με το backend (`_format_branch_header`).
    // Διαβάζουμε το user.court_branch και εξάγουμε το όνομα έδρας (αν περιφερειακή).
    // Defensive: διορθώνει typos όπως «Αμμαρ» → «Αμαρ» (διπλό μ).
    const stripTones = (s) => (s || "").normalize("NFD").replace(/\p{M}/gu, "");
    const fixTypos = (s) => (s || "").replace(
      /([αΑ])(μ|Μ)(μ|Μ)([αάΑΆ])?([ρΡ])/g,
      (_, a, m1, _m2, vowel, r) => a + m1 + (vowel || "") + r
    );
    const courtBranch = fixTypos((user?.court_branch || "").trim());
    let branchHeader = "ΠΡΩΤΟΔΙΚΕΙΟ ΑΘΗΝΩΝ ΚΕΝΤΡΙΚΗ ΕΔΡΑ";
    if (courtBranch) {
      const upper = stripTones(courtBranch.toUpperCase());
      if (upper !== "ΠΡΩΤΟΔΙΚΕΙΟ ΑΘΗΝΩΝ") {
        // Regional — εξαγωγή ονόματος πόλης μετά το "Έδρα"
        const m = courtBranch.match(/Έδρ[αας]+\s+(.+)$/i);
        const city = m ? m[1].trim() : courtBranch;
        branchHeader = `ΠΡΩΤΟΔΙΚΕΙΟ ΑΘΗΝΩΝ ΠΕΡΙΦΕΡΕΙΑΚΗ ΕΔΡΑ ${stripTones(city.toUpperCase())}`;
      }
    }
    return (
      `${branchHeader}\n\n` +
      `Αξιότιμε/η κύριε/α Πρόεδρε,\n\n` +
      `σας ενημερώνουμε ότι σας έχουν ανατεθεί οι παρακάτω υποθέσεις προκειμένου να ` +
      `διενεργήσετε Προδικαστικό Έλεγχο σύμφωνα με το άρθρο 237 ΚΠολΔ. ` +
      `Οι υποθέσεις που σας ανατέθηκαν είναι οι κάτωθι:\n\n` +
      `Α/Α ΓΑΚ/ΕΑΚ              ΕΤΟΣ    ΚΑΤΑΘΕΣΗ      ΟΛΟΚΛ. ΕΩΣ    \n` +
      "─".repeat(62) + "\n" +
      rows.join("\n") +
      `\n\nΣύνολο: ${selectedCases.length} υποθέσεις.\n` +
      `Η προθεσμία ολοκλήρωσης είναι 30 ημέρες από τον ορισμό σας.\n\n` +
      `Με εκτίμηση,\nΗ Γραμματεία του Δικαστηρίου`
    );
  };

  const openEmailDialog = () => {
    if (selectedCount === 0) { toast.error("Επιλέξτε τουλάχιστον μία υπόθεση"); return; }
    setEmailPreview(buildEmailPreview());
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailJudgeName?.trim()) { toast.error("Επιλέξτε δικαστή"); return; }
    if (!emailJudgeAddr?.trim()) { toast.error("Δώστε email δικαστή"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/pretrial-tools/send-judge-assignment-email`, {
        case_ids: Object.keys(selectedIds),
        judge_name: emailJudgeName.trim(),
        judge_email: emailJudgeAddr.trim(),
        court_type: courtType || "Μονομελές",
        auto_assign: emailAutoAssign,
        attach_composition_acts: courtType === "Πολυμελές" && emailAttachCompActs && !emailTextOnly,
        text_only: emailTextOnly,
      });
      toast.success(`Στάλθηκε σε ${res.data?.cases_updated || 0} υποθέσεις`);
      setShowEmailDialog(false);
      setSelectedIds({});
      fetchPinakia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία αποστολής email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Φίλτρα */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Κατάθεση από</Label>
              <DateInput value={filingFrom} onChange={e => setFilingFrom(e.target.value)} data-testid="ja-filing-from" />
            </div>
            <div>
              <Label className="text-xs">Κατάθεση έως</Label>
              <DateInput value={filingTo} onChange={e => setFilingTo(e.target.value)} data-testid="ja-filing-to" />
            </div>
            <div>
              <Label className="text-xs">Τύπος</Label>
              <Select value={courtType} onValueChange={setCourtType} disabled={userCourtTypes.length === 1}>
                <SelectTrigger data-testid="ja-court-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {userCourtTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Πινάκιο (αναζήτηση)</Label>
              <Input placeholder="π.χ. 412-ΑΜΑ" value={pinakioSearch} onChange={e => setPinakioSearch(e.target.value)} data-testid="ja-pinakio-search" />
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchPinakia} disabled={loading} data-testid="ja-search-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
                Αναζήτηση
              </Button>
              <Button variant="outline" onClick={() => { setSelectedIds({}); fetchPinakia(); }} data-testid="ja-refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Υπόμνημα χρωμάτων */}
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 flex-wrap">
            <span className="font-semibold">Υπόμνημα:</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-300 border border-emerald-500" />Ημέρες 1–3</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-300 border border-amber-500" />Ημέρες 4–9</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 border border-red-600" />Ημέρα 10 / Ληγμένη</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-300 border border-amber-500" /><span className="text-amber-800 font-medium">Εν αναμονή</span> (έχει ανατεθεί δικαστής αλλά δεν έχει κλείσει η προσθήκη)</span>
          </div>
        </CardContent>
      </Card>

      {/* Action bar */}
      <Card className={selectedCount > 0 ? "border-blue-300 bg-blue-50/30" : ""}>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-semibold">{selectedCount}</span> επιλεγμένες υποθέσεις από <span className="font-semibold">{pinakiaTree.length}</span> πινάκια
              {pinakiaTree.length > 0 && (
                <span className="text-slate-500 ml-2">
                  ({pinakiaTree.reduce((s, p) => s + p.total_cases, 0)} υποθέσεις συνολικά)
                </span>
              )}
            </div>
            <Button onClick={openEmailDialog} disabled={selectedCount === 0} data-testid="ja-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Αποστολή Email Ανάθεσης
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />Φόρτωση…</CardContent></Card>
      ) : pinakiaTree.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400"><AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" />Δεν βρέθηκαν πινάκια</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {pinakiaTree.map(pinakio => {
            const isOpen = !!expandedPinakio[pinakio.pinakio_label];
            const allCases = pinakio.hearings.flatMap(h => h.cases);
            const totalSel = allCases.filter(c => selectedIds[c.id]).length;
            const allSel = totalSel === pinakio.total_cases && totalSel > 0;
            const cardUrgency = aggregateUrgency(allCases);
            const pCounts = urgencyCounts(allCases);
            const pFinalized = pCounts.assigned;
            const pPending = pCounts.pending;
            return (
              <Card key={pinakio.pinakio_label} className={cardUrgency} data-testid={`ja-pinakio-${pinakio.pinakio_label}`}>
                <CardHeader className="py-3 cursor-pointer hover:bg-slate-50/50" onClick={() => togglePinakio(pinakio.pinakio_label)}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-wrap">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Gavel className="w-4 h-4 text-blue-600" />
                      <div className="min-w-0">
                        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                          {pinakio.pinakio_label}
                          <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                            Σύνολο: {pinakio.total_cases} υποθέσεις
                          </span>
                          <UrgencyCounters counts={pCounts} />
                        </CardTitle>
                        <CardDescription className="text-xs">
                          {pinakio.hearings.length} δικάσιμοι · {pinakio.court_type}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                      <Badge
                        variant="outline"
                        className={`text-xs ${pFinalized > 0 && pPending === 0 ? "border-green-300 text-green-700 bg-green-50" : ""}`}
                        title={pPending > 0 ? `${pFinalized} οριστικοποιημένες · ${pPending} εν αναμονή (δεν έχει κλείσει η προσθήκη)` : "Οριστικοποιημένες αναθέσεις"}
                      >
                        Οριστικ.: <span className="ml-1 font-semibold">{pFinalized}/{pinakio.total_cases}</span>
                        {pPending > 0 && (
                          <span className="ml-1 text-amber-700">· Εν αν. {pPending}</span>
                        )}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${pinakio.email_sent_count > 0 ? "border-green-300 text-green-700 bg-green-50" : ""}`}>
                        Email: <span className="ml-1 font-semibold">{pinakio.email_sent_count}/{pinakio.total_cases}</span>
                      </Badge>
                      <Checkbox checked={allSel} onCheckedChange={(v) => togglePinakioCases(pinakio, !!v)} data-testid={`ja-pinakio-checkbox-${pinakio.pinakio_label}`} />
                      <span className="text-xs text-slate-500">Όλες</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-emerald-700 hover:bg-emerald-50"
                        title="Εξαγωγή Excel πινακίου"
                        onClick={(e) => { e.stopPropagation(); downloadExport("xlsx", { pinakio: pinakio.pinakio_label }); }}
                        data-testid={`ja-export-xlsx-pinakio-${pinakio.pinakio_label}`}
                      >
                        <FileDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-700 hover:bg-red-50"
                        title="Εξαγωγή PDF πινακίου"
                        onClick={(e) => { e.stopPropagation(); downloadExport("pdf", { pinakio: pinakio.pinakio_label }); }}
                        data-testid={`ja-export-pdf-pinakio-${pinakio.pinakio_label}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      {user?.role === "admin" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-600 hover:text-red-800 hover:bg-red-50"
                          title="Διαγραφή όλων των υποθέσεων του πινακίου"
                          onClick={(e) => { e.stopPropagation(); confirmDeletePinakio(pinakio); }}
                          data-testid={`ja-delete-pinakio-${pinakio.pinakio_label}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isOpen && (
                  <CardContent className="pt-0 space-y-2">
                    {pinakio.hearings.map(h => {
                      const hOpen = !!expandedHearing[h.group_key];
                      const hSel = h.cases.filter(c => selectedIds[c.id]).length;
                      const hAllSel = hSel === h.total_cases && hSel > 0;
                      const hUrgency = aggregateUrgency(h.cases);
                      const hCounts = urgencyCounts(h.cases);
                      const hFinalized = hCounts.assigned;
                      const hPending = hCounts.pending;
                      return (
                        <div key={h.group_key} className={`border rounded-md ${hUrgency || "border-slate-200"}`} data-testid={`ja-hearing-${h.group_key}`}>
                          <div className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-white/60" onClick={() => toggleHearing(h.group_key)}>
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              {hOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <Calendar className="w-4 h-4 text-indigo-600" />
                              <span className="bg-indigo-100 text-indigo-800 text-xs font-mono px-2 py-0.5 rounded">
                                Δικάσιμος: {fmt(h.hearing_date)}
                              </span>
                              <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                                Σύνολο: {h.total_cases} υποθέσεις
                              </span>
                              <UrgencyCounters counts={hCounts} />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${hFinalized > 0 && hPending === 0 ? "border-green-300 text-green-700 bg-green-50" : ""}`}
                                title={hPending > 0 ? `${hFinalized} οριστικοποιημένες · ${hPending} εν αναμονή (δεν έχει κλείσει η προσθήκη)` : "Οριστικοποιημένες αναθέσεις"}
                              >
                                Ορ.: {hFinalized}/{h.total_cases}
                                {hPending > 0 && (
                                  <span className="ml-1 text-amber-700">· Εν αν. {hPending}</span>
                                )}
                              </Badge>
                              <Badge variant="outline" className={`text-[10px] ${h.email_sent_count > 0 ? "border-green-300 text-green-700 bg-green-50" : ""}`}>
                                Email: {h.email_sent_count}/{h.total_cases}
                              </Badge>
                              <Checkbox checked={hAllSel} onCheckedChange={(v) => toggleHearingCases(h.cases, !!v)} data-testid={`ja-hearing-checkbox-${h.group_key}`} />
                              <span className="text-[10px] text-slate-500">Όλες</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-emerald-700 hover:bg-emerald-50"
                                title="Excel δικασίμου"
                                onClick={(e) => { e.stopPropagation(); downloadExport("xlsx", { pinakio: pinakio.pinakio_label, hearing_date: h.hearing_date }); }}
                                data-testid={`ja-export-xlsx-hearing-${h.group_key}`}
                              >
                                <FileDown className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-700 hover:bg-red-50"
                                title="PDF δικασίμου"
                                onClick={(e) => { e.stopPropagation(); downloadExport("pdf", { pinakio: pinakio.pinakio_label, hearing_date: h.hearing_date }); }}
                                data-testid={`ja-export-pdf-hearing-${h.group_key}`}
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </Button>
                              {/* Ορισμός Δικαστή Προδικαστικού (bulk per πινάκιο + δικάσιμος) */}
                              <Button
                                variant="default"
                                size="sm"
                                className="h-6 px-2 text-[10px] bg-blue-600 hover:bg-blue-700 ml-1"
                                title="Ορισμός δικαστή για όλες τις υποθέσεις του πινακίου σε αυτή τη δικάσιμο"
                                onClick={(e) => { e.stopPropagation(); assignJudgeToPinakioHearing(pinakio.pinakio_label, h.hearing_date); }}
                                data-testid={`ja-assign-pinakio-${h.group_key}`}
                              >
                                <Gavel className="w-3 h-3 mr-0.5" />
                                Ορισμός Δικαστή
                              </Button>
                              {/* Bulk Πράξεις Ορισμού Δικαστή (Μονομελές + Πολυμελές) */}
                              <Button
                                variant="default"
                                size="sm"
                                className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 ml-1"
                                title="Bulk Πράξεις Ορισμού Δικαστή ή Σύνθεσης + auto-email (γραμματεία + δικαστής) + κλείδωμα"
                                onClick={(e) => { e.stopPropagation(); openBulkAssignment(pinakio.pinakio_label, h.hearing_date, h.cases || []); }}
                                data-testid={`ja-bulk-assignment-${h.group_key}`}
                              >
                                <Package className="w-3 h-3 mr-0.5" />
                                Πράξεις Δικαστή (ZIP+Email)
                              </Button>
                              {/* Εξαγωγή ZIP Πράξεων Δικασίμου ΧΩΡΙΣ Email */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-emerald-600 text-emerald-700 hover:bg-emerald-50 ml-1"
                                title="Παραγωγή & κατέβασμα ZIP Πράξεων Ορισμού ΧΩΡΙΣ αποστολή email"
                                onClick={(e) => { e.stopPropagation(); exportActsZipNoEmail(pinakio.pinakio_label, h.hearing_date, h.cases || []); }}
                                data-testid={`ja-bulk-assignment-noemail-${h.group_key}`}
                              >
                                <FileDown className="w-3 h-3 mr-0.5" />
                                ZIP χωρίς Email
                              </Button>
                              {/* Εκτύπωση Όλων των Πράξεων Δικασίμου (1 PDF, 1 πράξη/σελίδα) */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-blue-600 text-blue-700 hover:bg-blue-50 ml-1"
                                title="Εκτύπωση όλων των Πράξεων Ορισμού της δικασίμου σε ΕΝΑ PDF (1 πράξη ανά σελίδα Α4)"
                                onClick={(e) => { e.stopPropagation(); printAllActs(pinakio.pinakio_label, h.hearing_date); }}
                                data-testid={`ja-print-all-${h.group_key}`}
                              >
                                <Printer className="w-3 h-3 mr-0.5" />
                                Εκτύπωση Όλων
                              </Button>
                              {/* Επανέκδοση Πράξεων (διόρθωση ημερομηνιών) */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-amber-500 text-amber-700 hover:bg-amber-50 ml-1"
                                title="Επανέκδοση ήδη παραχθεισών Πράξεων με ΙΔΙΟΥΣ αριθμούς αλλά διορθωμένες ημερομηνίες"
                                onClick={(e) => { e.stopPropagation(); regenerateActsZip(pinakio.pinakio_label, h.hearing_date); }}
                                data-testid={`ja-regen-acts-${h.group_key}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-0.5" />
                                Επανέκδοση Πράξεων
                              </Button>
                              {/* Απαλοιφή Ανάθεσης δικαστή σε όλες τις υποθέσεις της δικασίμου */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-red-500 text-red-700 hover:bg-red-50 ml-1"
                                title="Απαλοιφή ανάθεσης δικαστή για όλες τις υποθέσεις του πινακίου σε αυτή τη δικάσιμο"
                                onClick={(e) => { e.stopPropagation(); openClearJudgeDialog(pinakio.pinakio_label, h.hearing_date); }}
                                data-testid={`ja-clear-pinakio-${h.group_key}`}
                              >
                                <Trash2 className="w-3 h-3 mr-0.5" />
                                Απαλοιφή Ανάθεσης
                              </Button>
                              {/* ΑΝΑΧΡΕΩΣΗ — αντικατάσταση δικαστή προδικαστικού + έδρας & ΠΡΑΞΗ ΝΕΟΥ ΟΡΙΣΜΟΥ (ZIP) */}
                              <Button
                                variant="default"
                                size="sm"
                                className="h-6 px-2 text-[10px] bg-orange-600 hover:bg-orange-700 ml-1"
                                title="ΑΝΑΧΡΕΩΣΗ: αντικατάσταση του δικαστή (προδικαστικού + έδρας) σε όλες/επιλεγμένες υποθέσεις της δικασίμου + Πράξεις Νέου Ορισμού (ZIP)"
                                onClick={(e) => { e.stopPropagation(); setReassignTarget({ pinakioLabel: pinakio.pinakio_label, hearingDate: h.hearing_date, courtType: courtType || undefined }); }}
                                data-testid={`ja-reassign-${h.group_key}`}
                              >
                                <UserCog className="w-3 h-3 mr-0.5" />
                                ΑΝΑΧΡΕΩΣΗ
                              </Button>
                              {/* Ενημερωτικό Δελτίο */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px] text-slate-700 ml-1"
                                title="Ενημερωτικό Δελτίο Δικασίμου (PDF)"
                                onClick={(e) => { e.stopPropagation(); downloadBulletin(pinakio.pinakio_label, h.hearing_date, "pdf"); }}
                                data-testid={`ja-bulletin-${h.group_key}`}
                              >
                                <FileText className="w-3 h-3 mr-0.5" />
                                Δελτίο
                              </Button>
                              {/* Bulk Πράξεις Ορισμού Εισηγητή (μόνο Πολυμελές) */}
                              {courtType === "Πολυμελές" && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-6 px-2 text-[10px] bg-purple-600 hover:bg-purple-700 ml-1"
                                  title="Bulk Πράξεις Ορισμού Εισηγητή για όλες τις υποθέσεις του πινακίου (ZIP)"
                                  onClick={(e) => { e.stopPropagation(); openBulkRapporteur(pinakio.pinakio_label, h.hearing_date); }}
                                  data-testid={`ja-bulk-rapporteur-${h.group_key}`}
                                >
                                  <Package className="w-3 h-3 mr-0.5" />
                                  Πράξεις Εισηγητή (ZIP)
                                </Button>
                              )}
                              {/* Lock indicator */}
                              {h.judge_locked && (
                                <Badge variant="outline" className="ml-1 h-6 px-2 text-[10px] border-amber-400 text-amber-700 bg-amber-50">
                                  🔒 Κλειδωμένο
                                </Badge>
                              )}
                              {/* Ημερολόγιο πινακίου + δικασίμου (XLSX / PDF) */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-purple-700 hover:bg-purple-50"
                                title="Ημερολόγιο προθεσμιών (Excel) με ειδοποιήσεις"
                                onClick={(e) => { e.stopPropagation(); exportPinakioCalendar(pinakio.pinakio_label, h.hearing_date, "xlsx"); }}
                                data-testid={`ja-cal-xlsx-${h.group_key}`}
                              >
                                <Calendar className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>

                          {hOpen && (
                            <div className="overflow-x-auto border-t border-slate-200">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-100 text-slate-700">
                                    <th className="p-2 w-10"></th>
                                    <th className="p-2 text-left">ΓΑΚ/ΕΑΚ · Έτος</th>
                                    <th className="p-2 text-left">Διάδικοι</th>
                                    <th className="p-2 text-center">Διάστημα Ορισμού</th>
                                    <th className="p-2 text-center">Επείγον</th>
                                    <th className="p-2 text-center">Όριο Προδικ.</th>
                                    <th className="p-2 text-left">Δικαστής</th>
                                    <th className="p-2 text-center">Email</th>
                                    <th className="p-2 text-center">Πράξη</th>
                                    <th className="p-2 text-center text-orange-800">ΑΡ. ΠΡΑΞΗΣ ΑΝΑΧΡΕΩΣΗΣ</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {h.cases.map(c => {
                                    const sent = !!c.judge_assignment_email_sent_at;
                                    const u = urgencyForJudgeWindow(c.judge_start, c.judge_end);
                                    const pending = isPendingAssignment(c);
                                    const rowSelected = !!selectedIds[c.id];
                                    return (
                                      <tr key={c.id} className={rowSelected ? "bg-blue-50" : (pending ? "bg-amber-50/30" : "")}>
                                        <td className="p-2 text-center">
                                          <Checkbox checked={rowSelected} onCheckedChange={() => toggleCase(c.id)} data-testid={`ja-case-checkbox-${c.id}`} />
                                        </td>
                                        <td className="p-2 font-mono">
                                          <div>ΓΑΚ {c.gak_number}/{c.gak_year}</div>
                                          <div className="text-xs text-slate-500">ΕΑΚ {c.eak_number || "—"}/{c.gak_year}</div>
                                        </td>
                                        <td className="p-2 text-xs">
                                          <div className="truncate max-w-[300px]"><span className="text-slate-400">Εν:</span> {anon(c.plaintiffs || "—")}</div>
                                          <div className="truncate max-w-[300px]"><span className="text-slate-400">Εγ:</span> {anon(c.defendants || "—")}</div>
                                        </td>
                                        <td className="p-2 text-center text-xs font-mono whitespace-nowrap">
                                          <div>{fmt(c.judge_start)}</div>
                                          <div className="text-slate-400">→ {fmt(c.judge_end)}</div>
                                        </td>
                                        <td className="p-2 text-center">
                                          {pending ? (
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] border-amber-400 text-amber-800 bg-amber-100"
                                              title={`Έχει ανατεθεί δικαστής αλλά δεν έχει κλείσει η προσθεσμία προσθήκης. Η Πράξη Ορισμού εκδίδεται την ${fmt(c.judge_start)}.`}
                                              data-testid={`ja-pending-badge-${c.id}`}
                                            >
                                              Εν αναμονή
                                            </Badge>
                                          ) : c.assigned_judge ? (
                                            <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50" data-testid={`ja-finalized-badge-${c.id}`}>Ανατεθ.</Badge>
                                          ) : u.label ? (
                                            <Badge className={`text-[10px] border ${u.cls}`}>{u.label}</Badge>
                                          ) : <span className="text-slate-300 text-xs">—</span>}
                                        </td>
                                        <td className="p-2 text-center text-xs font-mono">{fmt(c.pretrial_end)}</td>
                                        <td className="p-2">
                                          {c.assigned_judge ? (
                                            <div className="flex items-center gap-1">
                                              {pending ? (
                                                <span
                                                  className="text-amber-800 font-medium text-xs flex flex-col"
                                                  data-testid={`ja-pending-judge-${c.id}`}
                                                >
                                                  <span className="flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" /> {anon(c.assigned_judge)}
                                                  </span>
                                                  <span className="text-[10px] text-amber-700 font-mono">
                                                    Έκδοση: {fmt(c.judge_start)}
                                                  </span>
                                                </span>
                                              ) : (
                                                <span className="text-green-700 font-medium text-xs flex items-center gap-1">
                                                  <CheckCircle2 className="w-3 h-3" /> {anon(c.assigned_judge)}
                                                </span>
                                              )}
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-5 w-5 text-red-600 hover:bg-red-50"
                                                title="Απαλοιφή ανάθεσης δικαστή ΓΙΑ ΑΥΤΗ ΤΗΝ ΥΠΟΘΕΣΗ μόνο"
                                                onClick={(e) => { e.stopPropagation(); clearCaseAssignment(c); }}
                                                data-testid={`ja-clear-case-${c.id}`}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          ) : <span className="text-slate-300 text-xs">—</span>}
                                        </td>
                                        <td className="p-2 text-center">
                                          {sent ? (
                                            <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50 text-[10px]">
                                              <Mail className="w-3 h-3 mr-1" /> {fmt((c.judge_assignment_email_sent_at || "").substring(0,10))}
                                            </Badge>
                                          ) : <span className="text-slate-300 text-xs">—</span>}
                                        </td>
                                        <td className="p-2 text-center whitespace-nowrap">
                                          {c.assignment_act_number && (
                                            <div className="text-[10px] text-emerald-700 font-mono mb-1 flex items-center justify-center gap-0.5" data-testid={`ja-act-number-${c.id}`}>
                                              Αρ. {c.assignment_act_number}/{c.assignment_act_year || c.gak_year}
                                              <ActNumberButton caseId={c.id} kind="assignment" number={c.assignment_act_number} year={c.assignment_act_year || c.gak_year}
                                                gak={`${c.gak_number}/${c.gak_year}`} className="h-5 w-5"
                                                onSaved={(r) => { c.assignment_act_number = r.act_number; c.assignment_act_year = r.act_year; setGroupsRaw(prev => [...prev]); }} />
                                            </div>
                                          )}
                                          <div className="flex items-center justify-center gap-0.5">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 text-blue-700 hover:bg-blue-50"
                                              title="Πράξη Ορισμού (DOCX)"
                                              onClick={(e) => { e.stopPropagation(); generateAct(c, "docx"); }}
                                              data-testid={`ja-act-docx-${c.id}`}
                                            >
                                              <FileText className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 text-red-700 hover:bg-red-50"
                                              title="Πράξη Ορισμού (PDF)"
                                              onClick={(e) => { e.stopPropagation(); generateAct(c, "pdf"); }}
                                              data-testid={`ja-act-pdf-${c.id}`}
                                            >
                                              <FileDown className="w-3.5 h-3.5" />
                                            </Button>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center whitespace-nowrap" data-testid={`ja-reassign-cell-${c.id}`}>
                                          {c.reassignment_act_number ? (
                                            <>
                                              <div className="text-[10px] text-orange-800 font-mono mb-1" title={c.reassigned_from ? `Αντικατέστησε: ${anon(c.reassigned_from)}` : ""} data-testid={`ja-reassign-number-${c.id}`}>
                                                <span className="inline-flex items-center gap-0.5">
                                                  Αρ. {c.reassignment_act_number}/{c.reassignment_act_year || c.gak_year}
                                                  <ActNumberButton caseId={c.id} kind="reassignment" number={c.reassignment_act_number} year={c.reassignment_act_year || c.gak_year}
                                                    gak={`${c.gak_number}/${c.gak_year}`} className="h-5 w-5"
                                                    onSaved={(r) => { c.reassignment_act_number = r.act_number; c.reassignment_act_year = r.act_year; setGroupsRaw(prev => [...prev]); }} />
                                                </span>
                                                {c.reassigned_from && <span className="block text-slate-500 font-sans">από {anon(c.reassigned_from)}</span>}
                                              </div>
                                              <div className="flex items-center justify-center gap-0.5">
                                                <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-700 hover:bg-blue-50" title="Πράξη ΑΝΑΧΡΕΩΣΗΣ (DOCX)"
                                                        onClick={(e) => { e.stopPropagation(); downloadReassignmentAct(c.id, "docx"); }} data-testid={`ja-reassign-docx-${c.id}`}>
                                                  <FileText className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-700 hover:bg-red-50" title="Πράξη ΑΝΑΧΡΕΩΣΗΣ (PDF)"
                                                        onClick={(e) => { e.stopPropagation(); downloadReassignmentAct(c.id, "pdf"); }} data-testid={`ja-reassign-pdf-${c.id}`}>
                                                  <FileDown className="w-3.5 h-3.5" />
                                                </Button>
                                              </div>
                                            </>
                                          ) : <span className="text-slate-300 text-xs">—</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ReassignDialog target={reassignTarget} onClose={() => setReassignTarget(null)} onDone={() => fetchPinakia()} />

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Αποστολή Email Ανάθεσης Προδικαστικού Ελέγχου</DialogTitle>
            <DialogDescription>
              {selectedCount} υποθέσεις θα ανατεθούν στον δικαστή και θα ενημερωθεί με email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Δικαστής</Label>
                <Select value={emailJudgeName} onValueChange={(v) => {
                  setEmailJudgeName(v);
                  const j = judgesList.find(x => x.full_name === v);
                  if (j?.email) setEmailJudgeAddr(j.email);
                  setEmailPreview(buildEmailPreview());
                }}>
                  <SelectTrigger data-testid="ja-judge-select"><SelectValue placeholder="Επιλογή δικαστή" /></SelectTrigger>
                  <SelectContent>
                    {judgesList.map(j => (
                      <SelectItem key={j.id} value={j.full_name || j.username}>
                        <JudgeOptionLabel judge={j} />
                      </SelectItem>
                    ))}
                    {judgesList.length === 0 && (
                      <div className="text-xs text-slate-500 px-3 py-2">
                        Δεν βρέθηκαν δικαστές. Προσθέστε από Διαχείριση Χρηστών / Pretrial Judges.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Email Δικαστή</Label>
                <Input type="email" value={emailJudgeAddr} onChange={e => setEmailJudgeAddr(e.target.value)} placeholder="judge@example.gr" data-testid="ja-judge-email" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={emailAutoAssign} onCheckedChange={(v) => setEmailAutoAssign(!!v)} id="auto-assign-cb" data-testid="ja-auto-assign-cb" />
              <Label htmlFor="auto-assign-cb" className="text-sm cursor-pointer">
                Αυτόματη ανάθεση δικαστή στις επιλεγμένες υποθέσεις
              </Label>
            </div>
            {courtType === "Πολυμελές" && (
              <div className="flex items-center gap-2">
                <Checkbox checked={emailAttachCompActs && !emailTextOnly} disabled={emailTextOnly} onCheckedChange={(v) => setEmailAttachCompActs(!!v)} id="attach-comp-cb" data-testid="ja-attach-composition-acts-cb" />
                <Label htmlFor="attach-comp-cb" className="text-sm cursor-pointer">
                  Επισύναψη Πράξης Ανάθεσης σε Σύνθεση (PDF) για κάθε υπόθεση — το κείμενο του email προσαρμόζεται
                </Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox checked={emailTextOnly} onCheckedChange={(v) => setEmailTextOnly(!!v)} id="text-only-cb" data-testid="ja-text-only-cb" />
              <Label htmlFor="text-only-cb" className="text-sm cursor-pointer">
                Αποστολή μόνο του αυτόματου κειμένου, χωρίς συνημμένα αρχεία{courtType === "Μονομελές" ? " (χωρίς Δελτίο Δικασίμου)" : ""}
              </Label>
            </div>
            <div>
              <Label className="text-xs">Προεπισκόπηση Email</Label>
              <Textarea value={emailPreview} onChange={e => setEmailPreview(e.target.value)} rows={12} className="font-mono text-xs" data-testid="ja-email-preview" />
              <p className="text-[10px] text-slate-400 mt-1">
                Σημ.: Το πραγματικό email συντίθεται server-side. Η προεπισκόπηση είναι μόνο για επιβεβαίωση περιεχομένου.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)} disabled={sending}>Άκυρο</Button>
            <Button onClick={sendEmail} disabled={sending} data-testid="ja-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Αποστολή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Pinakio Confirmation (admin only) */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Διαγραφή Πινακίου
            </DialogTitle>
            <DialogDescription>
              Πρόκειται να διαγράψετε <strong>ολόκληρο το πινάκιο</strong> και <strong>όλες τις υποθέσεις</strong> που περιέχει.
              Η ενέργεια δεν αναιρείται.
            </DialogDescription>
          </DialogHeader>
          {pinakioToDelete && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1 text-sm">
              <div><strong>Πινάκιο:</strong> {pinakioToDelete.pinakio_label}</div>
              <div><strong>Σύνολο δικασίμων:</strong> {pinakioToDelete.hearings.length}</div>
              <div><strong>Σύνολο υποθέσεων προς διαγραφή:</strong> <span className="text-red-700 font-bold">{pinakioToDelete.total_cases}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>Άκυρο</Button>
            <Button variant="destructive" onClick={handleDeletePinakio} disabled={deleting} data-testid="ja-confirm-delete-pinakio">
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Οριστική Διαγραφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Πράξεις Ορισμού Εισηγητή Dialog */}
      <Dialog open={bulkRappOpen} onOpenChange={setBulkRappOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-700" />
              Bulk Πράξεις Ορισμού Εισηγητή
            </DialogTitle>
            <DialogDescription>
              Πινάκιο <strong>{bulkRappCtx.pinakio}</strong> · Δικάσιμος <strong>{fmt(bulkRappCtx.hearing_date)}</strong>.
              Παράγονται όλες οι Πράξεις Ορισμού Εισηγητή σε ZIP. Η <b>Ημερομηνία Έκδοσης</b> δηλώνεται ανά υπόθεση (προεπιλογή: πρώτη ημέρα μετά το κλείσιμο της Προσθήκης) και αποθηκεύεται στην υπόθεση.
            </DialogDescription>
          </DialogHeader>
          {bulkRappLoadingPrev ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : bulkRappPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div className="text-emerald-700 font-semibold">{bulkRappPreview.eligible}</div>
                  <div className="text-[10px] text-slate-500">Επιλέξιμες (έχουν Πράξη Σύνθεσης)</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-amber-700 font-semibold">{bulkRappPreview.skipped}</div>
                  <div className="text-[10px] text-slate-500">Θα παραλειφθούν</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="font-semibold">{bulkRappPreview.total}</div>
                  <div className="text-[10px] text-slate-500">Σύνολο πινακίου</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Αρχικός Αρ. Πράξης *</Label>
                  <Input
                    type="number" min="1"
                    value={bulkRappStartNum}
                    onChange={e => setBulkRappStartNum(e.target.value)}
                    data-testid="ja-bulk-rapp-start"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Θα δοθούν αυξανόμενοι αριθμοί στις υπόλοιπες υποθέσεις.</p>
                </div>
                <div>
                  <Label className="text-xs">Έτος Πράξης</Label>
                  <Input
                    value={bulkRappYear}
                    onChange={e => setBulkRappYear(e.target.value)}
                    placeholder="2026"
                    data-testid="ja-bulk-rapp-year"
                  />
                </div>
                <div>
                  <Label className="text-xs">Κοινή Ημερομηνία Έκδοσης σε όλες</Label>
                  <DateInput onChange={e => { const v = e.target.value; if (v) setBulkRappIssueDates(Object.fromEntries((bulkRappPreview.cases || []).map(c => [c.case_id, v]))); }} data-testid="ja-bulk-rapp-date-all" />
                </div>
                <div>
                  <Label className="text-xs">Format</Label>
                  <Select value={bulkRappFormat} onValueChange={setBulkRappFormat}>
                    <SelectTrigger data-testid="ja-bulk-rapp-format"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="docx">DOCX</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="odt">ODT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={bulkRappNote} onChange={e => setBulkRappNote(e.target.checked)} data-testid="ja-bulk-rapp-note" />
                <span>Συμπερίληψη της επισήμανσης «Επισημαίνεται ότι η νόμιμη προθεσμία … εκκινεί από …» στις Πράξεις</span>
              </label>

              <div className="max-h-80 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-700 sticky top-0">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">ΓΑΚ</th>
                      <th className="p-2 text-left">Τομέας</th>
                      <th className="p-2 text-left">Αντικείμενο</th>
                      <th className="p-2 text-center">Πράξη Σύνθεσης</th>
                      <th className="p-2 text-center">Ημερομηνία Έκδοσης *</th>
                      <th className="p-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRappPreview.cases.map((c, idx) => (
                      <tr key={c.case_id} className={`border-t ${c.eligible ? "" : "opacity-50"}`}>
                        <td className="p-2 text-slate-400">{idx + 1}</td>
                        <td className="p-2 font-mono">{c.gak}</td>
                        <td className="p-2">
                          {c.sector_number ? <Badge variant="outline" className="text-[10px]">{c.sector_number}ο</Badge> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-2 truncate max-w-[180px]" title={c.subject}>{c.subject}</td>
                        <td className="p-2 text-center font-mono">
                          {c.synthesis_act
                            ? <Badge className="bg-blue-600 text-white text-[9px]">{c.synthesis_act}</Badge>
                            : <span className="text-red-500">— χωρίς</span>}
                        </td>
                        <td className="p-2 text-center">
                          <DateInput value={bulkRappIssueDates[c.case_id] || ""} disabled={!c.eligible}
                                 onChange={e => setBulkRappIssueDates(d => ({ ...d, [c.case_id]: e.target.value }))}
                                 className="h-7 w-36 mx-auto text-xs" data-testid={`ja-bulk-rapp-date-${c.case_id}`} />
                        </td>
                        <td className="p-2 text-center">
                          {c.eligible
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" />
                            : <span className="text-amber-600 text-[10px]">skip</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-slate-500">
                Στο ZIP θα συμπεριληφθεί και αρχείο <code>SUMMARY.csv</code> με τα μεταδεδομένα όλων των πράξεων.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Δεν υπάρχουν δεδομένα</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkRappOpen(false)} disabled={bulkRappBusy}>Άκυρο</Button>
            <Button
              onClick={runBulkRapporteur}
              disabled={bulkRappBusy || bulkRappLoadingPrev || !bulkRappPreview || bulkRappPreview.eligible === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="ja-bulk-rapp-submit"
            >
              {bulkRappBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
              Παραγωγή ZIP ({bulkRappPreview?.eligible || 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk Πράξεις Ορισμού Δικαστή Dialog (auto-email + lock) */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-700" />
              {courtType === "Πολυμελές" ? "Bulk Πράξεις Ορισμού Σύνθεσης" : "Bulk Πράξεις Ορισμού Δικαστή"}
            </DialogTitle>
            <DialogDescription>
              Πινάκιο <strong>{bulkAssignCtx.pinakio}</strong> · Δικάσιμος <strong>{fmt(bulkAssignCtx.hearing_date)}</strong>.
              {bulkAssignCtx.case_ids && bulkAssignCtx.case_ids.length > 0 ? (
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-semibold">
                  Μόνο επιλεγμένες: {bulkAssignCtx.case_ids.length}
                </span>
              ) : null}
              {courtType === "Πολυμελές"
                ? (bulkAssignCtx.case_ids && bulkAssignCtx.case_ids.length > 0
                    ? " Κλειδώνεται η σύνθεση μόνο στις επιλεγμένες υποθέσεις."
                    : " Κλειδώνεται η σύνθεση σε όλες τις υποθέσεις της δικασίμου (όχι ο εισηγητής).")
                : (bulkAssignCtx.case_ids && bulkAssignCtx.case_ids.length > 0
                    ? " Κλειδώνεται ο δικαστής μόνο στις επιλεγμένες υποθέσεις."
                    : " Κλειδώνεται ο δικαστής σε όλες τις υποθέσεις της δικασίμου.")}
              {" "}Η <b>Ημερομηνία Έκδοσης</b> δηλώνεται ανά υπόθεση (προεπιλογή: πρώτη μέρα μετά το κλείσιμο της Προσθήκης) και αποθηκεύεται μαζί με την Πράξη.
            </DialogDescription>
          </DialogHeader>
          {bulkAssignLoadingPrev ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : bulkAssignPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-sm">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div className="text-emerald-700 font-semibold">{bulkAssignPreview.eligible}</div>
                  <div className="text-[10px] text-slate-500">Επιλέξιμες</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <div className="text-blue-700 font-semibold">{bulkAssignPreview.already_issued}</div>
                  <div className="text-[10px] text-slate-500">Με υπάρχουσα πράξη</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-amber-700 font-semibold">🔒 {bulkAssignPreview.locked}</div>
                  <div className="text-[10px] text-slate-500">Κλειδωμένες</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="font-semibold">{bulkAssignPreview.total}</div>
                  <div className="text-[10px] text-slate-500">Σύνολο</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Αρχικός Αρ. Πράξης (προαιρετικό)</Label>
                  <Input type="number" min="1" value={bulkAssignStartNum} onChange={e => setBulkAssignStartNum(e.target.value)} placeholder="(προεπιλογή 1)" data-testid="ja-bulk-assign-start" />
                </div>
                <div>
                  <Label className="text-xs">Έτος Πράξης</Label>
                  <Input value={bulkAssignYear} onChange={e => setBulkAssignYear(e.target.value)} placeholder="2026" data-testid="ja-bulk-assign-year" />
                </div>
                <div>
                  <Label className="text-xs">Κοινή Ημερομηνία Έκδοσης σε όλες</Label>
                  <DateInput onChange={e => { const v = e.target.value; if (v) setBulkAssignIssueDates(Object.fromEntries((bulkAssignPreview.cases || []).map(c => [c.case_id, v]))); }} data-testid="ja-bulk-assign-date-all" />
                </div>
                <div>
                  <Label className="text-xs">Format</Label>
                  <Select value={bulkAssignFormat} onValueChange={setBulkAssignFormat}>
                    <SelectTrigger data-testid="ja-bulk-assign-format"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="docx">DOCX</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="odt">ODT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border rounded p-3 bg-slate-50 space-y-2">
                <Label className="text-xs font-semibold">Δικαστής που θα κλειδωθεί στη δικάσιμο (προαιρετικό αλλά συνιστάται)</Label>
                <Select value={bulkAssignJudgeId} onValueChange={setBulkAssignJudgeId}>
                  <SelectTrigger data-testid="ja-bulk-assign-judge">
                    <SelectValue placeholder={`Επιλογή Δικαστή από τους ${bulkAssignJudges.length} καταχωρημένους (${courtType})`} />
                  </SelectTrigger>
                  <SelectContent>
                    {bulkAssignJudges.length === 0 ? (
                      <div className="p-2 text-xs text-slate-400 italic">Δεν υπάρχουν καταχωρημένοι Δικαστές Προδικαστικού. Πηγαίνετε στο tab Τομείς & Πράξεις.</div>
                    ) : bulkAssignJudges.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        <JudgeOptionLabel judge={j} showEmail />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={bulkAssignAutoEmail} onChange={e => setBulkAssignAutoEmail(e.target.checked)} data-testid="ja-bulk-assign-auto-email" />
                  <span>Αυτόματη αποστολή email σε Γραμματεία ({bulkAssignPreview.secretariat_emails?.join(", ") || "—"}) και στον Δικαστή</span>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={bulkAssignNote} onChange={e => setBulkAssignNote(e.target.checked)} data-testid="ja-bulk-assign-note" />
                  <span>Συμπερίληψη της επισήμανσης «Επισημαίνεται ότι η νόμιμη προθεσμία … εκκινεί από …» στις Πράξεις{courtType === "Πολυμελές" ? " (ισχύει για Πράξεις Ορισμού Εισηγητή για προδικαστικό έλεγχο — όχι για Πράξη Σύνθεσης)" : ""}</span>
                </label>
                {bulkAssignAutoEmail && (
                  <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div>
                      <Label className="text-xs font-semibold text-blue-900">
                        Επιπλέον Παραλήπτες Δικαστή (χειροκίνητα, χωρίζονται με κόμμα)
                      </Label>
                      <input
                        type="text"
                        value={bulkAssignExtraEmails}
                        onChange={(e) => setBulkAssignExtraEmails(e.target.value)}
                        placeholder="π.χ. judge1@example.gr, judge2@example.gr"
                        className="w-full mt-1 px-2 py-1.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="ja-bulk-extra-emails"
                      />
                      <p className="text-[10px] text-blue-700 mt-1">
                        Χρησιμοποιείται όταν το email του Δικαστή ΔΕΝ είναι στη βάση
                        ή αν θέλετε να σταλεί η Πράξη και σε άλλους.
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-blue-900">
                        Κοινοποίηση (CC) — σε όλα τα emails
                      </Label>
                      <input
                        type="text"
                        value={bulkAssignCcEmails}
                        onChange={(e) => setBulkAssignCcEmails(e.target.value)}
                        placeholder="π.χ. supervisor@example.gr"
                        className="w-full mt-1 px-2 py-1.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="ja-bulk-cc-emails"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 sticky top-0">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">ΓΑΚ</th>
                      <th className="p-2 text-left">Τομέας</th>
                      <th className="p-2 text-left">Αντικείμενο</th>
                      <th className="p-2 text-center">Κατάθεση</th>
                      <th className="p-2 text-center">Ημερομηνία Έκδοσης *</th>
                      <th className="p-2 text-center">Αρ.Πράξης</th>
                      <th className="p-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Pre-compute σειριακούς αριθμούς πράξης για ΥΠΟΘΕΣΕΙΣ ΧΩΡΙΣ act
                      // (ξεκινώντας από bulkAssignStartNum). Όσες ήδη έχουν αρ. πράξης
                      // διατηρούν τον υπάρχοντα — δεν επαναριθμούνται.
                      const startNum = parseInt(bulkAssignStartNum, 10) || 1;
                      let counter = startNum;
                      const yearStr = String(bulkAssignYear || new Date().getFullYear());
                      return bulkAssignPreview.cases.map((c, idx) => {
                        let displayNum;
                        if (c.has_act && c.existing_act) {
                          displayNum = c.existing_act;
                        } else if (c.eligible) {
                          displayNum = `${counter}/${yearStr}`;
                          counter += 1;
                        } else {
                          displayNum = "—";
                        }
                        return (
                          <tr key={c.case_id} className={`border-t ${c.eligible ? "" : "opacity-50"}`}>
                            <td className="p-2 text-slate-400">{idx + 1}</td>
                            <td className="p-2 font-mono">{c.gak}</td>
                            <td className="p-2">{c.sector_number ? <Badge variant="outline" className="text-[10px]">{c.sector_number}ο</Badge> : <span className="text-slate-300">—</span>}</td>
                            <td className="p-2 truncate max-w-[180px]" title={c.pretrial_warning ? `${c.subject || ""} — ${c.pretrial_warning}` : c.subject}>
                              {c.subject}
                              {c.pretrial_warning && <div className="text-[10px] text-orange-700 whitespace-normal mt-0.5" data-testid={`ja-bulk-warning-${c.case_id}`}>⚠ {c.case_type}: {c.pretrial_warning}</div>}
                            </td>
                            <td className="p-2 text-center font-mono">{fmt(c.filing_date)}</td>
                            <td className="p-2 text-center">
                              <DateInput value={bulkAssignIssueDates[c.case_id] || ""} disabled={!c.eligible && !c.has_act}
                                     onChange={e => setBulkAssignIssueDates(d => ({ ...d, [c.case_id]: e.target.value }))}
                                     className="h-7 w-36 mx-auto text-xs" data-testid={`ja-bulk-assign-date-${c.case_id}`} />
                            </td>
                            <td className="p-2 text-center font-mono font-semibold text-emerald-700">{displayNum}</td>
                            <td className="p-2 text-center text-[10px]">
                              {c.has_act && <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-700">Υφιστάμενη</Badge>}
                              {c.locked && <span title="Κλειδωμένο"> 🔒</span>}
                              {!c.has_act && c.eligible && <CheckCircle2 className="w-3 h-3 text-emerald-600 inline" />}
                              {!c.eligible && !c.has_act && <span className="text-amber-600">skip</span>}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500">
                <strong>Σημείωση:</strong> Στο ZIP περιλαμβάνεται και SUMMARY.csv. Όλες οι πράξεις παίρνουν αυξανόμενους αριθμούς.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Δεν υπάρχουν δεδομένα</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkAssignOpen(false)} disabled={bulkAssignBusy}>Άκυρο</Button>
            <Button
              onClick={runBulkAssignment}
              disabled={bulkAssignBusy || bulkAssignLoadingPrev || !bulkAssignPreview || ((bulkAssignPreview.total || 0) === 0)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ja-bulk-assign-submit"
            >
              {bulkAssignBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
              Παραγωγή ZIP{bulkAssignAutoEmail ? " + Auto-Email" : ""} ({((bulkAssignPreview?.eligible || 0) + (bulkAssignPreview?.already_issued || 0)) || 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActIssueDialog open={actDialog.open} onOpenChange={(o) => setActDialog(s => ({ ...s, open: o }))}
        title={`Πράξη Ορισμού ${courtType === "Πολυμελές" ? "Σύνθεσης" : "Δικαστή"} — ΓΑΚ ${actDialog.c?.gak_number || ""}/${actDialog.c?.gak_year || ""}`}
        description={actDialog.c?.assigned_judge ? `Δικαστής: ${actDialog.c.assigned_judge}` : ""}
        year={actDialog.c?.gak_year} defaultActNumber={actDialog.c?.assignment_act_number || ""}
        defaultIssueDate={(actDialog.c?.assignment_act_issue_date || "").slice(0, 10) || undefined}
        initialFormat={actDialog.format} onConfirm={confirmGenerateAct} testPrefix="ja-act-issue" showNoteOption
        noteHint={courtType === "Πολυμελές" ? "ισχύει για Πράξεις Ορισμού Εισηγητή για προδικαστικό έλεγχο, όχι για Πράξη Σύνθεσης" : ""} />

      {/* Judge Picker Dialog για Πινάκιο + Δικάσιμο */}
      <Dialog open={judgePickerState.open} onOpenChange={(o) => { if (!o) setJudgePickerState(s => ({ ...s, open: false })); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ορισμός Δικαστή Προδικαστικού Ελέγχου</DialogTitle>
            <DialogDescription>
              Πινάκιο <b>{judgePickerState.pinakio_label}</b> · Δικάσιμος <b>{judgePickerState.hearing_date}</b><br/>
              Ο επιλεγμένος δικαστής θα οριστεί σε <b>όλες</b> τις υποθέσεις του πινακίου για τη δικάσιμο.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <Label>Δικαστής Προδικαστικού Ελέγχου ({courtType})</Label>
            <Select value={judgePickerState.judge_id} onValueChange={(v) => setJudgePickerState(s => ({ ...s, judge_id: v }))}>
              <SelectTrigger data-testid="ja-judge-select">
                <SelectValue placeholder={pretrialJudgesList.length ? "Επιλέξτε δικαστή..." : `Δεν υπάρχουν δικαστές για ${courtType}`} />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {pretrialJudgesList.map(j => (
                  <SelectItem key={j.id} value={j.id}>
                    <JudgeOptionLabel judge={j} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pretrialJudgesList.length === 0 && (
              <p className="text-xs text-amber-700">
                ⚠ Προσθέστε δικαστές στην καρτέλα <b>Δικαστές Προδικαστικού</b>.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJudgePickerState(s => ({ ...s, open: false }))}>Άκυρο</Button>
            <Button onClick={confirmAssignJudgeFromPicker} disabled={judgePickerState.saving || !judgePickerState.judge_id} data-testid="ja-judge-confirm">
              {judgePickerState.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Ορισμός
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Απαλοιφή Ανάθεσης Δικαστή ανά πινάκιο+δικάσιμο */}
      <Dialog open={clearJudgeState.open} onOpenChange={(o) => { if (!o) setClearJudgeState(s => ({ ...s, open: false })); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Απαλοιφή Ανάθεσης Δικαστή
            </DialogTitle>
            <DialogDescription>
              Πινάκιο <b>{clearJudgeState.pinakio_label}</b> · Δικάσιμος <b>{clearJudgeState.hearing_date}</b><br/>
              Θα απαλειφθεί η ανάθεση δικαστή και οι Πράξεις Ορισμού για <b>όλες</b> τις υποθέσεις του πινακίου σε αυτή τη δικάσιμο.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label className="text-sm">Τρόπος Απαλοιφής:</Label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-2 border rounded hover:bg-slate-50">
                <input
                  type="radio"
                  name="clear-mode"
                  value="soft"
                  checked={clearJudgeState.mode === "soft"}
                  onChange={() => setClearJudgeState(s => ({ ...s, mode: "soft" }))}
                  data-testid="ja-clear-mode-soft"
                />
                <div className="text-sm">
                  <div className="font-semibold">Ήπια απαλοιφή (Soft)</div>
                  <div className="text-xs text-slate-600">Καθαρίζονται τα πεδία ανάθεσης ΑΛΛΑ καταγράφεται audit log (μπορεί να επανεμφανιστεί στο Ιστορικό Ακυρώσεων).</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 border rounded hover:bg-slate-50">
                <input
                  type="radio"
                  name="clear-mode"
                  value="hard"
                  checked={clearJudgeState.mode === "hard"}
                  onChange={() => setClearJudgeState(s => ({ ...s, mode: "hard" }))}
                  data-testid="ja-clear-mode-hard"
                />
                <div className="text-sm">
                  <div className="font-semibold text-red-700">Πλήρης Διαγραφή (Hard)</div>
                  <div className="text-xs text-slate-600">Καθαρίζονται τα πεδία ανάθεσης <b>ΟΡΙΣΤΙΚΑ</b>, χωρίς audit. (Μόνο admin/Συντονιστής)</div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearJudgeState(s => ({ ...s, open: false }))}>Άκυρο</Button>
            <Button onClick={confirmClearJudge} disabled={clearJudgeState.saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ja-clear-confirm">
              {clearJudgeState.saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Απαλοιφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JudgeAssignmentTab;
